import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, HelpCircle, User, LogOut } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button } from '@/components/ui/Button';
import { TokensBadge } from '@/features/billing/TokensBadge';
import { CartButton } from '@/features/billing/CartButton';
import { RoleBadges } from '@/features/billing/RolesAndBadges';
import { ThemeSwitcher } from '@/features/theme/ThemeSwitcher';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface BillingMe {
  tokens_balance: number;
  subscription_status: string;
  subscription_plan_id: number | null;
  subscription_expires_at: string | null;
  subscription_active: boolean;
}

export function TopBar() {
  const user = useAuthStore((s) => s.user);
  const patchBilling = useAuthStore((s) => s.patchBilling);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  // Refresh the billing snapshot whenever the AppShell mounts so a fresh
  // login (or an export from a previous tab) reflects the latest balance.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api
      .get<BillingMe>('/api/billing/me')
      .then((r) => {
        if (!alive) return;
        patchBilling({
          tokens_balance: r.data.tokens_balance,
          subscription_status: r.data.subscription_status,
          subscription_plan_id: r.data.subscription_plan_id,
          subscription_expires_at: r.data.subscription_expires_at,
        });
      })
      .catch(() => {
        /* non-fatal — the badge falls back to whatever was cached */
      });
    return () => {
      alive = false;
    };
    // We only need to re-fetch when the user id changes; tokens_balance is
    // intentionally excluded — refreshing on every balance update would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <header
      className="flex h-14 shrink-0 items-center gap-3 border-b px-4"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <div className="relative max-w-md flex-1">
        <Search
          size={14}
          strokeWidth={1.8}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }}
        />
        <input
          type="text"
          placeholder="Buscar proyectos, calculos, normas..."
          className="h-9 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--accent)] focus:outline-none"
          style={{ borderColor: 'var(--border-default)' }}
        />
      </div>

      <div className="ml-auto flex items-center gap-1">
        <ThemeSwitcher />
        <TokensBadge />
        <CartButton />
        <Button variant="ghost" size="icon" aria-label="Ayuda">
          <HelpCircle size={16} strokeWidth={1.8} />
        </Button>
        <Button variant="ghost" size="icon" aria-label="Notificaciones">
          <Bell size={16} strokeWidth={1.8} />
        </Button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="ml-2 flex h-9 items-center gap-2 rounded-[var(--radius-pill)] border px-3 text-xs hover:bg-[var(--surface-2)]"
              style={{
                background: 'var(--surface-2)',
                borderColor: 'var(--border-default)',
                color: 'var(--text-secondary)',
              }}
            >
              <User size={14} strokeWidth={1.8} aria-hidden />
              <span className="font-mono tabular">{user?.email ?? 'Invitado'}</span>
              {user && <RoleBadges user={user} />}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[200px] overflow-hidden rounded-[var(--radius-md)] border p-1 shadow-lg"
              style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
            >
              {user && (
                <div className="border-b px-3 py-2 text-xs" style={{ borderColor: 'var(--border-subtle)' }}>
                  <div style={{ color: 'var(--text-primary)' }}>{user.name || user.email}</div>
                  <div className="font-mono tabular text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {user.email}
                  </div>
                </div>
              )}
              <DropdownMenu.Item
                onSelect={() => {
                  logout();
                  navigate('/login');
                }}
                className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-xs outline-none hover:bg-[var(--surface-2)]"
                style={{ color: 'var(--text-secondary)' }}
              >
                <LogOut size={12} strokeWidth={1.8} /> Cerrar sesion
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
